import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'

const STORAGE_KEY = 'stark.incognito'

interface IncognitoContextValue {
  incognito: boolean
  setIncognito: (next: boolean) => void
  toggle: () => void
}

const IncognitoContext = createContext<IncognitoContextValue | null>(null)

function read(): boolean {
  if (typeof window === 'undefined') return false
  return window.localStorage.getItem(STORAGE_KEY) === '1'
}

export function IncognitoProvider({ children }: { children: ReactNode }) {
  const [incognito, setIncognitoState] = useState<boolean>(() => read())

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (incognito) window.localStorage.setItem(STORAGE_KEY, '1')
    else window.localStorage.removeItem(STORAGE_KEY)
  }, [incognito])

  const setIncognito = useCallback((next: boolean) => setIncognitoState(next), [])
  const toggle = useCallback(() => setIncognitoState((p) => !p), [])

  return (
    <IncognitoContext.Provider value={{ incognito, setIncognito, toggle }}>
      {children}
    </IncognitoContext.Provider>
  )
}

export function useIncognito(): IncognitoContextValue {
  const ctx = useContext(IncognitoContext)
  if (!ctx) throw new Error('useIncognito must be used inside IncognitoProvider')
  return ctx
}

/** Read-only helper for code outside the React tree (api hooks, etc). */
export function isIncognitoEnabled(): boolean {
  return read()
}
